import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import {
  CurrencyDollarIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  EyeIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  BanknotesIcon,
  UserIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline'
import { Payment, PaymentStatus } from '../../types'
import { paymentService } from '../../services/payment.service'

export default function VerifiedPaymentsPage() {
  const [payments, setPayments] = useState<Payment[]>([])
  const [filteredPayments, setFilteredPayments] = useState<Payment[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filter states
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'verifiedAt' | 'amount' | 'caseNumber'>('verifiedAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // Modal states
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null)
  const [showReceiptModal, setShowReceiptModal] = useState(false)

  useEffect(() => {
    loadVerifiedPayments()
  }, [])

  useEffect(() => {
    filterAndSortPayments()
  }, [payments, searchTerm, sortBy, sortOrder])

  // Auto-refresh when window regains focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !isLoading) {
        loadVerifiedPayments()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [isLoading])

  const loadVerifiedPayments = async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Get all payments and filter for verified ones (status = PAID with verificationStatus = VERIFIED)
      const allPayments = await paymentService.getAllBankTransferPayments()
      const verifiedPayments = allPayments.filter(p =>
        p.status === 'PAID' &&
        p.verificationStatus === 'VERIFIED'
      )

      setPayments(verifiedPayments)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load verified payments')
    } finally {
      setIsLoading(false)
    }
  }

  const filterAndSortPayments = () => {
    let filtered = payments && Array.isArray(payments) ? [...payments] : []

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.funeralCase?.caseNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.funeralCase?.deceasedName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.user?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.user?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.verificationNotes?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Sort payments
    filtered.sort((a, b) => {
      let comparison = 0

      switch (sortBy) {
        case 'verifiedAt':
          const aDate = a.verifiedAt ? new Date(a.verifiedAt).getTime() : 0
          const bDate = b.verifiedAt ? new Date(b.verifiedAt).getTime() : 0
          comparison = aDate - bDate
          break
        case 'amount':
          comparison = a.amount - b.amount
          break
        case 'caseNumber':
          const aCase = a.funeralCase?.caseNumber || ''
          const bCase = b.funeralCase?.caseNumber || ''
          comparison = aCase.localeCompare(bCase)
          break
      }

      return sortOrder === 'asc' ? comparison : -comparison
    })

    setFilteredPayments(filtered)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-MY', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusIcon = (status: PaymentStatus): string => {
    return paymentService.getPaymentStatusIcon(status)
  }

  const getStatusColor = (status: PaymentStatus): string => {
    return paymentService.getPaymentStatusColor(status)
  }

  const viewReceipt = (payment: Payment) => {
    setSelectedPayment(payment)
    setShowReceiptModal(true)
  }

  const ReceiptModal = () => {
    if (!selectedPayment) return null

    const receiptUrl = selectedPayment.transferProofUrl || selectedPayment.receiptUrl

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
          <div
            className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
            onClick={() => setShowReceiptModal(false)}
          ></div>

          <div className="relative inline-block w-full max-w-4xl p-6 my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Payment Receipt - Case {selectedPayment.funeralCase?.caseNumber}
              </h3>
              <button
                onClick={() => setShowReceiptModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Amount</p>
                  <p className="text-lg font-semibold text-gray-900">{formatCurrency(selectedPayment.amount)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Verified Date</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {selectedPayment.verifiedAt ? formatDate(selectedPayment.verifiedAt) : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Verification Notes</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {selectedPayment.verificationNotes || 'No notes provided'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Verified By</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {selectedPayment.verifiedBy ? 'Admin' : 'N/A'}
                  </p>
                </div>
              </div>

              {receiptUrl ? (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">Receipt Image</p>
                  <div className="border rounded-lg overflow-hidden">
                    <img
                      src={receiptUrl}
                      alt="Payment receipt"
                      className="w-full h-auto max-h-96 object-contain bg-gray-50"
                      onError={(e) => {
                        e.currentTarget.src = '/placeholder-receipt.png'
                      }}
                    />
                  </div>
                  <div className="mt-2">
                    <a
                      href={receiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Open receipt in new tab →
                    </a>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">No receipt available</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setShowReceiptModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const getStats = () => {
    const totalAmount = payments.reduce((sum, p) => sum + p.amount, 0)
    const thisMonth = payments.filter(p => {
      const verifiedDate = p.verifiedAt ? new Date(p.verifiedAt) : null
      if (!verifiedDate) return false
      const now = new Date()
      return verifiedDate.getMonth() === now.getMonth() &&
             verifiedDate.getFullYear() === now.getFullYear()
    }).length

    return {
      total: payments.length,
      totalAmount,
      thisMonth
    }
  }

  const stats = getStats()

  if (isLoading && (!payments || payments.length === 0)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Verified Payments</h1>
        <p className="mt-2 text-gray-600">
          View and manage completed payment verifications
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 mb-8 sm:grid-cols-3">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Verified</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">{stats.total}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BanknotesIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Amount</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(stats.totalAmount)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">This Month</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">{stats.thisMonth}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search by case number, name, or notes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="flex space-x-2">
              <select
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
              >
                <option value="verifiedAt">Sort by Date</option>
                <option value="amount">Sort by Amount</option>
                <option value="caseNumber">Sort by Case Number</option>
              </select>

              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>

              <button
                onClick={loadVerifiedPayments}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ArrowPathIcon className="w-4 h-4 mr-1" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Payment List */}
        <div className="overflow-hidden">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {filteredPayments && filteredPayments.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {filteredPayments.map((payment) => (
                <div key={payment.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(payment.status as PaymentStatus)}`}>
                          {getStatusIcon(payment.status as PaymentStatus)}
                          {paymentService.formatPaymentStatus(payment.status as PaymentStatus)}
                        </div>
                        <CheckBadgeIcon className="w-5 h-5 text-green-600" title="Verified" />
                      </div>

                      <div className="mt-2">
                        <h3 className="text-lg font-medium text-gray-900">
                          Case {payment.funeralCase?.caseNumber} - {payment.funeralCase?.deceasedName}
                        </h3>
                        <p className="text-sm text-gray-600">{payment.description}</p>
                      </div>

                      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-900">Amount:</span>
                          <div className="text-lg font-semibold text-green-600">
                            {formatCurrency(payment.amount)}
                          </div>
                        </div>
                        <div>
                          <span className="font-medium text-gray-900">Customer:</span>
                          <div className="text-gray-600">
                            {payment.user?.firstName} {payment.user?.lastName}
                          </div>
                        </div>
                        <div>
                          <span className="font-medium text-gray-900">Verified:</span>
                          <div className="text-gray-600">
                            {payment.verifiedAt ? formatDate(payment.verifiedAt) : 'N/A'}
                          </div>
                        </div>
                        <div>
                          <span className="font-medium text-gray-900">Notes:</span>
                          <div className="text-gray-600 truncate" title={payment.verificationNotes || 'No notes'}>
                            {payment.verificationNotes || 'No notes'}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="ml-4 flex space-x-2">
                      {(payment.transferProofUrl || payment.receiptUrl) && (
                        <button
                          onClick={() => viewReceipt(payment)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <EyeIcon className="w-4 h-4 mr-1" />
                          View Receipt
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No verified payments</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm ? 'No verified payments match your search criteria.' : 'No payments have been verified yet.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Receipt Modal */}
      {showReceiptModal && <ReceiptModal />}
    </div>
  )
}